export type StageId =
  | "trigger"
  | "signal"
  | "inspiration"
  | "compression"
  | "release"
  | "recovery";

export type LayerId =
  | "surface"
  | "respiratory"
  | "larynx"
  | "mouth"
  | "ocular"
  | "nervous"
  | "pressure";

export type Confidence = "high" | "moderate" | "limited";

export interface Source {
  id: string;
  short: string;
  title: string;
  url: string;
  evidence: string;
}

export interface SneezeStage {
  id: StageId;
  order: number;
  navLabel: string;
  subLabel: string;
  playbackMs: number;
  eyebrow: string;
  title: string;
  summary: string;
  narration: string;
  physiologicTiming: string;
  metric: { value: string; label: string };
  confidence: Confidence;
  confidenceLabel: string;
  evidenceNote: string;
  evidence: string[];
  sourceIds: string[];
  defaultLayers: LayerId[];
  focus: "head" | "brainstem" | "thorax" | "larynx" | "airway" | "body";
  structure: {
    name: string;
    system: string;
    role: string;
  };
}

export interface Myth {
  claim: string;
  verdict: "False" | "Misleading" | "Not established";
  explanation: string;
  sourceIds: string[];
}

export const TOTAL_PLAYBACK_MS = 72_000;

export const stages: SneezeStage[] = [
  {
    id: "trigger",
    order: 1,
    navLabel: "Trigger",
    subLabel: "Nasal mucosa · CN V",
    playbackMs: 9_000,
    eyebrow: "01 · Detection",
    title: "The nose detects an intruder.",
    summary:
      "An irritant reaches sensory endings just beneath the nasal surface.",
    narration:
      "A particle touches the nasal lining. Sensory endings of the trigeminal nerve detect the irritation and begin a protective reflex.",
    physiologicTiming:
      "Variable — the nasal tickle can build before the motor sneeze begins.",
    metric: { value: "CN V", label: "primary sensory route" },
    confidence: "high",
    confidenceLabel: "Established anatomy",
    evidenceNote:
      "The trigger can be mechanical, chemical, allergic, or infection-related. The particle does not travel to the brain; a nerve signal does.",
    evidence: [
      "Trigeminal sensory fibers innervate the nasal mucosa.",
      "Environmental irritants can activate these peripheral endings.",
    ],
    sourceIds: ["silver2009"],
    defaultLayers: ["surface", "nervous"],
    focus: "head",
    structure: {
      name: "Nasal mucosa",
      system: "Upper airway",
      role: "The sensory surface where irritation recruits trigeminal endings.",
    },
  },
  {
    id: "signal",
    order: 2,
    navLabel: "Signal",
    subLabel: "V1 / V2 · medulla",
    playbackMs: 11_000,
    eyebrow: "02 · Coordination",
    title: "The brainstem assembles the reflex.",
    summary:
      "Sensory input becomes one coordinated command for the airway, face, chest, and abdomen.",
    narration:
      "The alert travels into the brainstem. A sneeze-related medullary network recruits breathing, laryngeal, facial, and abdominal muscles as one motor pattern.",
    physiologicTiming:
      "Exact signal timing and the complete human circuit are not resolved.",
    metric: { value: "Medulla", label: "coordination region" },
    confidence: "moderate",
    confidenceLabel: "Human localization · emerging mechanism",
    evidenceNote:
      "Human lesion evidence localizes a sneeze-related area near the lateral medulla. The detailed molecular pathway shown in research was established primarily in mice.",
    evidence: [
      "Human clinical evidence implicates the lateral medulla near the spinal trigeminal nucleus.",
      "The most detailed nose-to-respiratory-network circuit is animal evidence.",
    ],
    sourceIds: ["seijo2006", "li2021"],
    defaultLayers: ["surface", "nervous"],
    focus: "brainstem",
    structure: {
      name: "Sneeze-related medullary network",
      system: "Nervous system",
      role: "Coordinates sensory input with respiratory, laryngeal, and facial output.",
    },
  },
  {
    id: "inspiration",
    order: 3,
    navLabel: "Inhale",
    subLabel: "Lungs · diaphragm",
    playbackMs: 11_000,
    eyebrow: "03 · Preparation",
    title: "The lungs load the breath.",
    summary:
      "The glottis opens while the diaphragm descends and the thorax expands.",
    narration:
      "The glottis opens. The diaphragm contracts and descends, the rib cage expands, and the lungs draw in the air that will power the sneeze.",
    physiologicTiming: "About 1,000 ms in one classic aerodynamic study.",
    metric: { value: "≈1.0 s", label: "reported inspiration" },
    confidence: "high",
    confidenceLabel: "Measured motor phase",
    evidenceNote:
      "The 11-second screen chapter is deliberate slow motion. The measured preparatory inspiration is roughly one second, with substantial person-to-person variation.",
    evidence: [
      "A preparatory inspiration precedes compression and expulsion.",
      "The diaphragm descends during inspiration; it does not contract upward.",
    ],
    sourceIds: ["unno1975", "unno1989"],
    defaultLayers: ["surface", "respiratory", "larynx", "pressure"],
    focus: "thorax",
    structure: {
      name: "Lungs and diaphragm",
      system: "Respiratory pump",
      role: "Load the air volume and mechanical energy used during expulsion.",
    },
  },
  {
    id: "compression",
    order: 4,
    navLabel: "Compress",
    subLabel: "Glottis · abdomen · eyes",
    playbackMs: 13_000,
    eyebrow: "04 · Pressure",
    title: "The glottis seals; pressure climbs.",
    summary:
      "Expiratory muscles contract against closed vocal folds, briefly minimizing outward airflow.",
    narration:
      "The vocal folds close while expiratory muscles squeeze the chest and abdomen. Pressure rises behind the glottis, and the eyelids close with an accompanying blink.",
    physiologicTiming: "Approximately 200–300 ms reported.",
    metric: { value: "0.2–0.3 s", label: "reported compression" },
    confidence: "moderate",
    confidenceLabel: "Measured glottis · eye motion extrapolated",
    evidenceNote:
      "The glottis—not the epiglottis or a plugged nose—is the pressure seal. Globe motion is subtle and extrapolated from blink studies because sneeze-specific eye kinematics are not well measured.",
    evidence: [
      "The vocal folds adduct while expiratory muscles raise intrathoracic pressure.",
      "Blink studies support small, gaze-dependent globe movement rather than a dramatic upward roll.",
    ],
    sourceIds: ["unno1989", "collewijn1985"],
    defaultLayers: [
      "surface",
      "respiratory",
      "larynx",
      "ocular",
      "pressure",
    ],
    focus: "larynx",
    structure: {
      name: "Vocal folds and glottis",
      system: "Larynx",
      role: "Create the temporary occlusion against which expiratory pressure builds.",
    },
  },
  {
    id: "release",
    order: 5,
    navLabel: "Release",
    subLabel: "Airflow · outlet route varies",
    playbackMs: 18_000,
    eyebrow: "05 · Expulsion",
    title: "The seal opens in milliseconds.",
    summary:
      "Stored pressure becomes a short, fast jet through the upper airway.",
    narration:
      "The glottis opens abruptly. Stored pressure and muscle force produce a short jet through the mouth and/or nose; the route varies. In one human study, airflow peaked within twenty milliseconds.",
    physiologicTiming:
      "Around 0.43–1.0 seconds across human studies; peak flow can occur in 20 ms.",
    metric: { value: "20 ms", label: "to measured peak flow" },
    confidence: "high",
    confidenceLabel: "High-speed human measurement",
    evidenceNote:
      "One ten-participant study measured maximum airflow near 15.9 m/s. That is not a universal sneeze speed, and mouth-versus-nose routing varies. The plume is representative, not a prediction of droplet count or infection risk.",
    evidence: [
      "Abrupt glottal opening converts built-up pressure into rapid expiration.",
      "High-speed imaging shows fluid sheets and ligaments fragmenting into droplets outside the mouth.",
    ],
    sourceIds: ["han2021", "unno1975", "unno1989", "scharfman2016"],
    defaultLayers: [
      "surface",
      "respiratory",
      "larynx",
      "mouth",
      "ocular",
      "pressure",
    ],
    focus: "airway",
    structure: {
      name: "Oronasal outflow",
      system: "Upper airway",
      role: "Routes a rapidly decaying jet through the mouth and/or nose; the split varies.",
    },
  },
  {
    id: "recovery",
    order: 6,
    navLabel: "Recover",
    subLabel: "Pressure normalizes",
    playbackMs: 10_000,
    eyebrow: "06 · Reset",
    title: "The system returns to breathing.",
    summary:
      "Airflow falls, pressure dissipates, and recruited structures move toward baseline.",
    narration:
      "Airflow falls, pressure normalizes, and breathing muscles return toward rest. The eyelids reopen; if irritation remains, another sneeze may follow.",
    physiologicTiming: "Variable — no universal human recovery duration.",
    metric: { value: "Variable", label: "reset time" },
    confidence: "moderate",
    confidenceLabel: "Established sequence · variable duration",
    evidenceNote:
      "Recovery is not a fixed timer. Persistent irritation can recruit the reflex again, producing a cluster of sneezes.",
    evidence: [
      "Airflow and thoracic pressure return toward ordinary breathing conditions.",
      "The eyes return toward their pre-blink position as the eyelids reopen.",
    ],
    sourceIds: ["unno1989", "collewijn1985"],
    defaultLayers: ["surface", "respiratory", "larynx", "ocular"],
    focus: "body",
    structure: {
      name: "Respiratory system",
      system: "Whole-body reflex",
      role: "Relaxes toward baseline after the coordinated motor discharge.",
    },
  },
];

export const sources: Source[] = [
  {
    id: "silver2009",
    short: "Silver & Finger · 2009",
    title:
      "The anatomical and electrophysiological basis of peripheral nasal trigeminal chemoreception",
    url: "https://pubmed.ncbi.nlm.nih.gov/19686138/",
    evidence: "Nasal trigeminal endings and irritant detection.",
  },
  {
    id: "li2021",
    short: "Li et al. · Cell · 2021",
    title:
      "Sneezing reflex is mediated by a peptidergic pathway from nose to brainstem",
    url: "https://pubmed.ncbi.nlm.nih.gov/34133943/",
    evidence: "Mouse NMB/NMBR spinal-trigeminal-to-respiratory mechanism.",
  },
  {
    id: "seijo2006",
    short: "Seijo-Martínez et al. · 2006",
    title:
      "Sneeze related area in the medulla: localisation of the human sneezing centre?",
    url: "https://pubmed.ncbi.nlm.nih.gov/16354739/",
    evidence: "Human lateromedullary clinical localization.",
  },
  {
    id: "unno1975",
    short: "Unno · 1975",
    title: "Aerodynamics of Sneezing",
    url: "https://doi.org/10.1016/S0385-8146(75)80003-4",
    evidence: "Three aerodynamic phases, flow, volume, and oral/nasal routes.",
  },
  {
    id: "unno1989",
    short: "Unno & Nonaka · 1989",
    title: "Laryngeal Role in Sneezing",
    url: "https://doi.org/10.5426/larynx1989.1.1_26",
    evidence: "Phase timing, glottal occlusion, and muscle activity.",
  },
  {
    id: "han2021",
    short: "Han et al. · 2021",
    title:
      "Experimental measurements of airflow features and velocity distribution exhaled from sneeze and speech",
    url: "https://pubmed.ncbi.nlm.nih.gov/34908645/",
    evidence: "Human airflow duration, peak timing, and velocity.",
  },
  {
    id: "collewijn1985",
    short: "Collewijn et al. · 1985",
    title: "Eye movements during blinks and prolonged eyelid closure",
    url: "https://pubmed.ncbi.nlm.nih.gov/4031978/",
    evidence: "Measured movement of the eyes beneath closed lids.",
  },
  {
    id: "scharfman2016",
    short: "Scharfman et al. · 2016",
    title:
      "Visualization of sneeze ejecta: steps of fluid fragmentation leading to respiratory droplets",
    url: "https://doi.org/10.1007/s00348-015-2078-4",
    evidence: "High-speed sheet, ligament, and droplet fragmentation.",
  },
];

export const myths: Myth[] = [
  {
    claim: "The eyes always roll dramatically upward.",
    verdict: "Misleading",
    explanation:
      "Short-blink measurements support small, gaze-dependent movement; dramatic upward rolling is not universal and is not established for sneezing.",
    sourceIds: ["collewijn1985"],
  },
  {
    claim: "Every sneeze travels at 100 mph.",
    verdict: "Misleading",
    explanation:
      "Airflow, droplet velocity, and plume-front velocity are different measurements. Controlled human studies vary substantially.",
    sourceIds: ["han2021"],
  },
  {
    claim: "The epiglottis slams shut to build pressure.",
    verdict: "False",
    explanation:
      "The measured occlusive structure is the glottis between the vocal folds.",
    sourceIds: ["unno1989"],
  },
];

export const stageStartTimes = stages.reduce<number[]>((times, _stage, index) => {
  times[index] = index === 0 ? 0 : times[index - 1] + stages[index - 1].playbackMs;
  return times;
}, []);

export function getStageAtTime(timeMs: number) {
  const clamped = Math.max(0, Math.min(timeMs, TOTAL_PLAYBACK_MS - 1));
  let elapsed = 0;

  for (let index = 0; index < stages.length; index += 1) {
    elapsed += stages[index].playbackMs;
    if (clamped < elapsed) {
      return { stage: stages[index], index, stageStartMs: stageStartTimes[index] };
    }
  }

  return {
    stage: stages[stages.length - 1],
    index: stages.length - 1,
    stageStartMs: stageStartTimes[stages.length - 1],
  };
}

export const medicalNotice =
  "Educational visualization, not diagnosis or medical advice. Anatomy, timing, airflow, and muscle recruitment vary between people and individual sneezes.";

export const visualizationNotice =
  "Neural pulses, pressure colors, diaphragm motion, and airborne particles are explanatory overlays—not patient-recorded motion or literal particle counts.";
